import React from 'react';
import styles from './CheckoutEmailPage.scss';

import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import * as Sentry from '@sentry/browser';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { LinkVariant } from 'src/api/contentful/contentTypes.cloverdotcom';
import { Button } from 'src/components/Button/Button';
import { useCart } from 'src/components/Cart/UseCart';
import { CheckoutRoutePath } from 'src/components/Checkout/Checkout';
import { emailPattern } from 'src/components/Contact/regex';
import { FormWrapper } from 'src/components/Form/FormWrapper';
import { useSalesChannel } from 'src/components/SalesChannel/SalesChannel';
import { TextV2 } from 'src/components/TextV2/TextV2';
import { redesignV2RoutePath } from 'src/constants/redesignV2';
import { fetchContentfulCookieConfig, makeMiscInfoJsonString } from 'src/services/analytics/analytics';
import { generateEmail, generateLead } from 'src/services/shop/checkout';
import { getField } from 'src/utils/EntryUtil';
import { useCheckout } from '../CheckoutProvider';
import { generateCartRequestObject } from '../Utils';

export interface CheckoutEmailFields {
  emailAddress: string;
}

interface FormErrors {
  emailAddress?: string;
}

export const CheckoutEmailPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { emptyCart } = useCart();

  const { userInfo, emailAddress, selectedPurchaseType, setEmailAddress, setIsLoading, setHasApiError } = useCheckout();
  const { cart, checkout } = useCart();
  const { salesChannel } = useSalesChannel();

  const onBackButton = () => {
    const reviewPage = `${redesignV2RoutePath.PURCHASE}${CheckoutRoutePath.REVIEW}`;
    history.push(reviewPage);
  };

  const onSubmitForm = async (values: CheckoutEmailFields) => {
    setEmailAddress(values.emailAddress);

    try {
      setIsLoading(true);

      const cookieConfig = await fetchContentfulCookieConfig();
      const marketingInfo = JSON.parse(makeMiscInfoJsonString(cookieConfig));
      const resellerId = getField(salesChannel, 'cloverResellerId');

      if (!userInfo) throw new Error('Cannot submit cart. Missing: User Information');
      if (!resellerId) throw new Error('Cannot submit cart. Missing: Reseller ID');

      const cartRequestObject = generateCartRequestObject({
        cart,
        checkout,
        userInfo,
        emailAddress: values.emailAddress,
        marketingInfo,
        resellerId,
        selectedPurchaseType,
      });
      const leadResponse = await generateLead(cartRequestObject);
      const leadId = leadResponse.data.uuid;
      await generateEmail(leadId);

      const statusPage = `${redesignV2RoutePath.PURCHASE}${CheckoutRoutePath.STATUS}`;
      history.push(statusPage);
      emptyCart();
    } catch (e) {
      const message = e.response?.data?.message || e.message || 'Unknown error';
      console.error('Error submitting lead:', message);
      Sentry.captureException(e);
      setHasApiError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = (values: CheckoutEmailFields) => {
    const errors: FormErrors = {};
    if (!values.emailAddress) {
      errors.emailAddress = t('emailAddress.required');
    } else if (!emailPattern.test(values.emailAddress)) {
      errors.emailAddress = t('emailAddress.invalid');
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: {
      emailAddress,
    },
    validateOnBlur: true,
    validate: validateForm,
    onSubmit: onSubmitForm,
  });

  const emailAddressValue = formik.values.emailAddress.replace(/\s+/g, '');

  return (
    <section className={styles.checkoutEmailPage}>
      <header className={styles.checkoutEmailPageHeader}>
        <TextV2
          className={styles.checkoutEmailPageHeading}
          tagName="h2"
          variant="h2-bold"
          value={t('checkout-email-page.title')}
        />
        <TextV2
          tagName="h5"
          variant="body"
          value={t('checkout-email-page.description')}
        />
      </header>
      <FormWrapper>
        <form className={styles.form} onSubmit={formik.handleSubmit}>
          <FormControl
            fullWidth
          >
            <TextField
              autoFocus
              id="email-address"
              inputProps={{
                'data-test': 'email-address',
              }}
              name="emailAddress"
              label={t('emailAddress.label')}
              value={emailAddressValue}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              helperText={formik.touched.emailAddress && formik.errors.emailAddress}
              error={!!(formik.touched.emailAddress && formik.errors.emailAddress)}
            />
          </FormControl>
          <div className={styles.formActions}>
            <Button
              data-test="email-address-back"
              onClick={onBackButton}
              variant={LinkVariant.SECONDARY}
            >
              {t('back')}
            </Button>
            <Button
              data-test="email-address-submit"
              type="submit"
              variant={LinkVariant.PRIMARY}
              disabled={!emailAddressValue || !!formik.errors.emailAddress}
            >
              {t('checkout-email-page.submit-btn-label')}
            </Button>
          </div>
        </form>
      </FormWrapper>
    </section>
  );
};
